// ─────────────────────────────────────────────────────────────
// Connects to the /admin Socket.io namespace.
// New alerts arrive instantly via WebSocket.
// Falls back to polling every 60s if socket disconnects.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from "react";
import { io }              from "socket.io-client";
import { getAdminAlerts }  from "../api/apiServices";

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || "https://serve.easwallet.com";

export function useAdminAlerts({ token, enabled = true } = {}) {
  const [alerts,      setAlerts]      = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected,   setConnected]   = useState(false);
  const [loading,     setLoading]     = useState(true);
  const socketRef = useRef(null);
  const pollRef   = useRef(null);

  // ── Initial load from REST ────────────────────────────────
  const loadAlerts = useCallback(async () => {
    try {
      const res = await getAdminAlerts({ dismissed: "false", limit: 100 });
      if (res.status) {
        setAlerts(res.data || []);
        setUnreadCount(res.unreadCount || 0);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  // ── Socket.io connection ──────────────────────────────────
  useEffect(() => {
    if (!enabled || !token) return;

    loadAlerts();

    const socket = io(`${SOCKET_URL}/admin`, {
      auth:              { token: `Bearer ${token}` },
      transports:        ["websocket"],
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      clearInterval(pollRef.current); // stop polling once socket is live
    });

    socket.on("disconnect", () => {
      setConnected(false);
      // Fall back to polling every 60s while disconnected
      pollRef.current = setInterval(loadAlerts, 60_000);
    });

    // ── New alert pushed from server ──────────────────────
    socket.on("new_alert", (alert) => {
      setAlerts(prev => {
        // Avoid duplicates (in case of reconnect race)
        if (prev.some(a => a._id === alert._id)) return prev;
        return [alert, ...prev];
      });
      setUnreadCount(c => c + 1);
    });

    // ── Alert dismissed by another admin tab ──────────────
    socket.on("alert_dismissed", ({ _id }) => {
      if (_id === "all") {
        setAlerts([]);
        setUnreadCount(0);
      } else {
        setAlerts(prev => prev.filter(a => a._id !== _id));
        setUnreadCount(c => Math.max(0, c - 1));
      }
    });

    return () => {
      socket.disconnect();
      clearInterval(pollRef.current);
    };
  }, [enabled, token, loadAlerts]);

  // ── Local dismiss (optimistic) ────────────────────────────
  const dismissLocal = (id) => {
    if (id === "all") {
      setAlerts([]);
      setUnreadCount(0);
    } else {
      setAlerts(prev => prev.filter(a => a._id !== id));
      setUnreadCount(c => Math.max(0, c - 1));
    }
  };

  const criticalCount = alerts.filter(a => a.severity === "critical").length;

  return {
    alerts,
    unreadCount,
    criticalCount,
    connected,
    loading,
    refresh:      loadAlerts,
    dismissLocal,
  };
}
